import React, { useEffect, useState } from 'react';
import { ledgerService, userService, skillService } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import CategorySubcategorySelect from '../common/CategorySubcategorySelect';
import { Container, Paper, Typography, Box, TextField, Button, Alert, Autocomplete, Chip, CircularProgress } from '@mui/material';
import { Link } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

const RegistraScambio = () => {
  const { currentUser } = useAuth();
  const isAdminMod = currentUser?.role === 'admin' || currentUser?.role === 'moderator' || currentUser?.role === 'superadmin';
  const [form, setForm] = useState({ providerId: '', receiverId: '', categoryId: '', subcategoryId: '', hours: '', description: '' });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [users, setUsers] = useState([]);
  const [allowedPairs, setAllowedPairs] = useState(null);
  const [loading, setLoading] = useState(false); // <-- Aggiunto
  const [receiverBalance, setReceiverBalance] = useState(null);
  const [receiverBalanceLoading, setReceiverBalanceLoading] = useState(false);
  const [receiverBalanceFetchError, setReceiverBalanceFetchError] = useState(false);

  useEffect(() => {
    if (currentUser?.id) setForm(prev => ({ ...prev, providerId: String(currentUser.id) }));
    const loadUsers = async () => {
      try {
        const res = await userService.getChoices();
        // Il backend già ordina alfabeticamente; manteniamo comunque un sort di sicurezza
        const sorted = [...res.data].sort((a, b) => {
          const la = (a.last_name || '').localeCompare(b.last_name || '');
          if (la !== 0) return la;
          const fa = (a.first_name || '').localeCompare(b.first_name || '');
          if (fa !== 0) return fa;
          return (a.email || '').localeCompare(b.email || '');
        });
        setUsers(sorted);
      } catch (e) { /* noop */ }
    };
    loadUsers();
  }, [currentUser]);

  useEffect(() => {
    const receiverId = form.receiverId;
    if (!receiverId) {
      setReceiverBalance(null);
      setReceiverBalanceFetchError(false);
      return;
    }
    let cancelled = false;
    const loadReceiverBalance = async () => {
      setReceiverBalanceLoading(true);
      setReceiverBalanceFetchError(false);
      try {
        const res = await ledgerService.getAccountForUser(receiverId);
        if (cancelled) return;
        setReceiverBalance(Number(res.data?.balance_hours));
      } catch (e) {
        if (cancelled) return;
        setReceiverBalance(null);
        setReceiverBalanceFetchError(true);
      } finally {
        if (!cancelled) setReceiverBalanceLoading(false);
      }
    };
    loadReceiverBalance();
    return () => { cancelled = true; };
  }, [form.receiverId]);

  // Carica le skill del fornitore e costruisce la mappa allowedPairs
  useEffect(() => {
    const loadSkillsForProvider = async () => {
      const pid = form.providerId || (currentUser?.id ? String(currentUser.id) : '');
      if (!pid) return;
      try {
        const params = (isAdminMod && String(currentUser.id) !== String(pid)) ? { user: pid } : undefined;
        const res = await skillService.list(params);
        const pairs = {};
        for (const s of res.data) {
          const cid = String(s.category);
          const sid = s.subcategory ? String(s.subcategory) : null;
          if (!pairs[cid]) pairs[cid] = [];
          if (sid) pairs[cid].push(sid);
        }
        setAllowedPairs(pairs);
      } catch (e) { /* noop */ }
    };
    loadSkillsForProvider();
    // Quando cambia fornitore, azzera categoria/subcategoria
    setForm(prev => ({ ...prev, categoryId: '', subcategoryId: '' }));
  }, [form.providerId, isAdminMod, currentUser]);

  const minBalance = -3;
  const formatHours = (v) => {
    if (typeof v !== 'number' || Number.isNaN(v)) return '';
    return v.toFixed(2).replace(/\.00$/, '').replace(/(\.\d)0$/, '$1');
  };
  const hoursValue = Number(form.hours) || 0;
  const receiverAtLimit = typeof receiverBalance === 'number' && receiverBalance <= minBalance;
  const projectedReceiverBalance = typeof receiverBalance === 'number' ? receiverBalance - hoursValue : null;
  const receiverWouldGoUnder = typeof projectedReceiverBalance === 'number' && hoursValue > 0 && projectedReceiverBalance < minBalance;
  const receiverBalanceUnavailable = Boolean(form.receiverId) && (receiverBalanceLoading || receiverBalanceFetchError);
  const maxReceivableHours = typeof receiverBalance === 'number' ? Math.max(0, receiverBalance - minBalance) : null;
  const canRegister = !receiverBalanceUnavailable && !receiverAtLimit && !receiverWouldGoUnder;

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true); // <-- Inizio loading
    if (form.providerId && form.receiverId && String(form.providerId) === String(form.receiverId)) {
      setError('Fornitore e ricevente non possono coincidere');
      setLoading(false);
      return;
    }
    if (!form.providerId || !form.receiverId || !form.categoryId || !form.hours) {
      setError('Compila tutti i campi obbligatori');
      setLoading(false);
      return;
    }
    if (receiverWouldGoUnder || receiverAtLimit) {
      if (receiverAtLimit) {
        setError('Saldo insufficiente: il ricevente è già a -3 ore e non può scambiare altre ore.');
      } else {
        setError(`Saldo insufficiente: il ricevente può scambiare al massimo ${formatHours(Number(maxReceivableHours))} h.`);
      }
      setLoading(false);
      return;
    }
    try {
      await ledgerService.createTransaction({
        provider: Number(form.providerId),
        receiver: Number(form.receiverId),
        category: Number(form.categoryId),
        subcategory: form.subcategoryId ? Number(form.subcategoryId) : null,
        hours: Number(form.hours),
        description: form.description || '',
      });
      setSuccess('Transazione creata. In attesa di approvazione.');
      setForm({ providerId: '', receiverId: '', categoryId: '', subcategoryId: '', hours: '', description: '' });
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.response?.data?.error;
      const receiverMsg = e?.response?.data?.receiver?.[0];
      setError(receiverMsg || detail || 'Errore nella creazione della transazione');
    } finally {
      setLoading(false); // <-- Fine loading
    }
  };

  return (
    <Container component="main" maxWidth={false} disableGutters>
      <Paper
        elevation={3}
        sx={{
          p: { xs: 2, sm: 3, md: 4 },
          mt: { xs: 4, md: 8 },
          width: '100%',
          maxWidth: { xs: '100%', md: 900 },
          mx: 'auto',
          minWidth: 0,
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{ display: 'flex', flexDirection: 'column', alignItems: { xs: 'flex-start', sm: 'center' }, justifyContent: 'center', position: 'relative', mb: 3 }} >
          <Button  component={Link} to="/dashboard" startIcon={<ArrowBackIcon />} variant="outlined" color="primary"
            sx={{ alignSelf: { xs: 'flex-start', sm: 'flex-start' }, mb: { xs: 1, sm: 0 }, textTransform: 'none', }} >
            Dashboard
          </Button>

          <Typography component="h1" variant="h4" gutterBottom
            sx={{ textAlign: { xs: 'left', sm: 'center' }, width: '100%', }} >
            Registra Scambio
          </Typography>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {receiverBalanceUnavailable && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {receiverBalanceLoading ? 'Controllo saldo ricevente…' : 'Impossibile verificare il saldo del ricevente. Riprova.'}
          </Alert>
        )}
        {(receiverAtLimit || receiverWouldGoUnder) && !receiverBalanceUnavailable && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {receiverAtLimit
              ? 'Attenzione: il ricevente è già a -3 ore e non può scambiare altre ore.'
              : `Attenzione: il ricevente può scambiare al massimo ${formatHours(Number(maxReceivableHours))} h.`}
          </Alert>
        )}
        {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}
        <Box component="form" onSubmit={submit} sx={{ display: 'grid', gap: 2, minWidth: 0 }}>
          {isAdminMod ? (
            <Autocomplete
              sx={{
                minWidth: 0,
                '& .MuiInputBase-root': { minWidth: 0 },
                '& input': { minWidth: 0, textOverflow: 'ellipsis' },
              }}
              options={users.filter(u => u.is_active)}
              getOptionLabel={(option) => `${option.email} (${option.first_name || ''} ${option.last_name || ''})`}
              value={(users.filter(u => u.is_active).find(u => String(u.id) === String(form.providerId))) || null}
              onChange={(_, val) => setForm({ ...form, providerId: val ? String(val.id) : '' })}
              renderInput={(params) => <TextField {...params} label="Fornitore (provider)" fullWidth />}
            />
          ) : (
            <TextField label="Fornitore (provider)" value={`${currentUser?.email || ''}`} disabled fullWidth />
          )}
          <Autocomplete
            sx={{
              minWidth: 0,
              '& .MuiInputBase-root': { minWidth: 0 },
              '& input': { minWidth: 0, textOverflow: 'ellipsis' },
            }}
            options={users.filter(u => u.is_active && (isAdminMod || String(u.id) !== String(form.providerId || (currentUser?.id ? String(currentUser.id) : ''))))}
            getOptionLabel={(option) => `${option.email} (${option.first_name || ''} ${option.last_name || ''})`}
            value={(users.filter(u => u.is_active && (isAdminMod || String(u.id) !== String(form.providerId || (currentUser?.id ? String(currentUser.id) : '')))).find(u => String(u.id) === String(form.receiverId))) || null}
            onChange={(_, val) => setForm({ ...form, receiverId: val ? String(val.id) : '' })}
            renderInput={(params) => <TextField {...params} label="Ricevente (receiver)" fullWidth />}
          />
          <CategorySubcategorySelect
            value={{ categoryId: form.categoryId, subcategoryId: form.subcategoryId }}
            onChange={(v) => setForm({ ...form, categoryId: v.categoryId, subcategoryId: v.subcategoryId })}
            allowedPairs={allowedPairs}
          />
          <TextField label="Ore" type="number" inputProps={{ step: '0.25', min: '0' }} value={form.hours} onChange={(e) => setForm({ ...form, hours: e.target.value })} fullWidth />
          <TextField label="Descrizione" multiline minRows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} fullWidth />
          <Box>
            <Button
              type="submit"
              variant="contained"
              disabled={loading || !canRegister}
              startIcon={loading ? <CircularProgress size={20} /> : null}
            >
              {loading ? 'Invio...' : 'Registra'}
            </Button>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
};

export default RegistraScambio;
